import { useTranslation } from 'react-i18next';
import { PostBody } from 'types/post';

import React, { useEffect, useRef, useState } from 'react';

import { Button } from '@thinkific/chalk/react-components';

import { EditorContent } from '@tiptap/react';

import { useGlobalState } from '../../providers/GlobalStateProvider';
import { autolinkEmailsInJSON } from '../hooks/useEditor/helpers';
import { useEditor } from '../hooks/useEditor/useEditor';
import * as css from './Content.css';

type ContentProps = {
  body: PostBody;
  className?: string;
  id: string;
  collapseContent?: boolean;
};

export default function Content({ body, className, id, collapseContent = true }: ContentProps) {
  const { t } = useTranslation();
  const { isContentExpanded, expandCollapseContent } = useGlobalState();
  const [showExpandButton, setShowExpandButton] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { editor, content: editorContent } = useEditor({
    content: body?.jsonContent ? autolinkEmailsInJSON(body.jsonContent) : undefined,
    version: 'readonly',
    className,
    shouldUpdate: body?.jsonContent,
  });

  useEffect(() => {
    if (!editorContent || !collapseContent || !ref?.current) return;
    setShowExpandButton(
      editorContent.length > 480 ||
        ((ref.current.firstElementChild as HTMLElement)?.offsetHeight || 0) > 100
    );
  }, [editorContent]);

  return (
    <div className={css.container} data-qa="post-content">
      <EditorContent
        className={css.content?.[isContentExpanded(id) ? 'expanded' : 'collapsed'] || ''}
        editor={editor}
        ref={ref}
      />
      {showExpandButton && (
        <Button
          className={css.expandButton}
          dataQa="post-content__expand-button"
          emphasis="lowCompact"
          fullWidth={false}
          size="sm"
          onClick={() => expandCollapseContent(id)}
        >
          {isContentExpanded(id) ? t('Read less') : t('Read more')}
        </Button>
      )}
    </div>
  );
}
